<template>
  <b-modal :title="helper.trans('labels.auto_transfer_settings')"
           :modelValue="modal !== null"
           footer-class=""
           @update:modelValue="$event === false && (modal = null)">
    <template #default>
      <LoaderWrapper ref="loader">
        <div v-if="requestDTO">
          <div class="row" v-if="account && account.isAutoWithdrawalEnabled">
            <div class="col-sm-12">
              <div class="alert alert-danger">{{ helper.trans('labels.autoTransferWarningText')}}</div>
            </div>
          </div>
          <div class="row">
            <label for="isAutoTransferEnabled" class="col-sm-6 col-form-label text-capitalize-first">
              <span v-if="requestDTO.isAutoTransferEnabled">{{ helper.trans("labels.isAutoTransferEnabled") }}</span>
              <span v-else>{{ helper.trans("labels.isAutoTransferDisabled") }}</span>
            </label>
            <div class="col-sm-6 d-flex align-items-center">
              <div class="form-check form-switch">
                <input v-model="requestDTO.isAutoTransferEnabled" :disabled="account?.isAutoWithdrawalEnabled" class="form-check-input" type="checkbox" id="isAutoTransferEnabled">
              </div>
            </div>
          </div>

          <div class="row mt-2" v-if="requestDTO.isAutoTransferEnabled">
            <div class="col-sm-12 d-flex justify-content-between align-items-center pb-0">
              <label for="minSum" class="col-form-label text-capitalize-first">{{ helper.trans("labels.autoTransferMinSum") }}</label>
            </div>
            <div class="col-sm-12">
              <FormInputWrapper v-slot="parentProps" :errors="helper.getErrors('autoTransfer', 'minSum')" >
                <input v-model="requestDTO.minSum"
                       v-maska
                       :disabled="account?.isAutoWithdrawalEnabled"
                       data-maska="9 99#"
                       data-maska-tokens="9:[0-9]:repeated"
                       data-maska-reversed
                       :class="parentProps.class"
                       type="text" class="form-control " id="minSum">
              </FormInputWrapper>
            </div>
          </div>

          <div class="row mt-2" v-if="requestDTO.isAutoTransferEnabled">
            <label for="targetAccountId" class="col-sm-12 col-form-label text-capitalize-first">
              {{ helper.trans("labels.target_account") }}
            </label>
            <div class="col-sm-12">
              <FormInputWrapper v-slot="parentProps" :errors="helper.getErrors('autoTransfer','targetAccountId')">
                <Multiselect :class="parentProps.class"
                             class="multiselect"
                             v-model="requestDTO.targetAccountId"
                             :options="targetAccounts"
                             :custom-label="(opt) => labelFor(opt)"
                             track-by="account.id"
                             select-label="Select"
                             deselect-label=""
                             :disabled="account?.isAutoWithdrawalEnabled"
                             placeholder=""
                             open-direction="bottom"
                             id="targetAccountId"
                             :multiple="false"
                             :searchable="true"
                             :clear-on-select="true"
                             :close-on-select="true"
                             :show-no-results="false"
                             :hide-selected="false"
                >
                </Multiselect>
              </FormInputWrapper>
            </div>
          </div>
        </div>
      </LoaderWrapper>
    </template>
    <template #footer>
      <div class="d-flex justify-content-between w-100">
        <button type="button" @click="modal = null" class="btn btn-outline-secondary btn-sm text-capitalize-first">
          {{ helper.trans('button.cancel') }}
        </button>
        <LoaderWrapper ref="saveLoader" :zoom="0.5">
        <button type="button" @click="save()" class="btn btn-outline-primary btn-sm text-capitalize-first">
          {{ helper.trans('button.save') }}
        </button>
        </LoaderWrapper>
      </div>
    </template>
  </b-modal>
</template>

<script lang="ts">

import {computed, defineComponent, ref, PropType, nextTick} from 'vue'
import {reactive, toRefs} from "@vue/reactivity";
import {BModal} from "bootstrap-vue-3";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {helper} from "@/service/CommonHelper";
import APIClient from "@/service/APIClient";
import {useNotification} from "@kyvg/vue3-notification";
import {numberFormatter} from "@/service/NumberFormatter";
import ValidationErrorDTO from "@/models/ValidationErrorDTO";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import Multiselect from "vue-multiselect";
import AccountDTO from "@/models/AccountDTO";
import MerchantStoreAccountDTO from "@/models/MerchantStoreAccountDTO";
import MerchantStoreDTO from "@/models/MerchantStoreDTO";

const api = new APIClient();
const notification = useNotification()

interface AutoTransferRequestDTO {
  isAutoTransferEnabled: boolean;
  minSum?: string | null;
  targetAccountId?: MerchantStoreAccountDTO | null;
}

export default defineComponent({
  components: {
    BModal,
    LoaderWrapper,
    FormInputWrapper,
    Multiselect
  },
  props: {
    stores: {
      type: Array as PropType<MerchantStoreDTO[]>,
      default: () => []
    }
  },
  emits: ['account:updated'],
  setup(props, {emit}) {

    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const saveLoader = ref<InstanceType<typeof LoaderWrapper> | null>(null)

    const state = reactive({
      modal: null as boolean|null,
      requestDTO: null as AutoTransferRequestDTO|null,
      account: null as AccountDTO|null,
      targetAccounts: [] as MerchantStoreAccountDTO[],
      errors: {} as ValidationErrorDTO[]|any,
    });

    const getForm = () => {
      return {}
    }

    const show = (account: AccountDTO) => {
      state.modal = true
      state.account = account
      state.requestDTO = {
        isAutoTransferEnabled: account.isAutoTransferEnabled || false,
        minSum: account.autoTransferMinSum ? numberFormatter.formatMoney(account.autoTransferMinSum, 0) : null,
        targetAccountId: null // Инициализируем как null
      } as AutoTransferRequestDTO
      state.errors = {}

      loadTargetAccounts()

        if (account.autoTransferTargetAccountId) {
          const targetAccount = state.targetAccounts.find(item => item.account.id === account.autoTransferTargetAccountId)
          if (targetAccount && state.requestDTO) {
            nextTick(() => {
              if (state.requestDTO) {
                state.requestDTO.targetAccountId = targetAccount
              }
            })
          }
        }
    }

    const setErrors = (data: ValidationErrorDTO[]) => {
      state.errors = {};
      for (const error of data) {
        if (!error.field) {
          notification.notify({type: 'error', title: error.message});
          continue;
        }
        if (!state.errors.hasOwnProperty(error.field)) {
          state.errors[error.field] = [];
        }
        state.errors[error.field].push(error)
      }
    }

    const save = async () => {
      if (state.account === null) {
        return;
      }

      saveLoader.value?.show()
      try {
        const targetAccountId = state.requestDTO?.targetAccountId?.account?.id || null
        
        state.account = await api.accountsAutoTransfer(state.account.id, {
          isAutoTransferEnabled: state.requestDTO!.isAutoTransferEnabled,
          minSum: state.requestDTO?.minSum
            ? Number.parseFloat(state.requestDTO!.minSum!.replace(/\s/g, ''))
            : null,
          targetAccountId: targetAccountId
        })
        emit('account:updated', state.account)
        state.modal = null
        notification.notify({
          title: helper.trans('labels.auto_transfer_settings_saved'),
          type: 'success'
        })
      } catch (e: any) {
        if (e.response && e.request && e.request.status === 400 && e.response.data) {
          setErrors(e.response.data)
        } else {
          helper.handleErrors('autoTransfer', e)
        }
      } finally {
        saveLoader.value?.hide()
      }
    }

    const loadTargetAccounts = () => {
      if (state.account === null) {
        return;
      }

      // Получаем все аккаунты магазинов с той же валютой из пропсов
      const allAccounts: MerchantStoreAccountDTO[] = []
      props.stores.forEach((store: MerchantStoreDTO) => {
        store.accounts.forEach((storeAccount: MerchantStoreAccountDTO) => {
          allAccounts.push(storeAccount)
        })
      })

      // Фильтруем, исключая текущий аккаунт и оставляя только с той же валютой
      state.targetAccounts = allAccounts.filter(item => 
        item.account.id !== state.account?.id && 
        item.account.currency === state.account?.currency
      )
      

    }

    const labelFor = (account: MerchantStoreAccountDTO) => {
      // Находим магазин по storeId чтобы получить его валюту
      const store = props.stores.find(store => store.id === account.storeId)
      const storeCurrency = store?.currency || account.account.currency
      return `${account.storeName} (${storeCurrency.toUpperCase()}) - ${account.account.id.substring(0, 8)}...`
    }

    return {
      ...toRefs(state),
      show,
      helper,
      getForm,
      numberFormatter,
      loader,
      save,
      saveLoader,
      labelFor
    }
  }
})
</script> 